import React, { createContext, useContext, useEffect, useState } from 'react';
import { User, onAuthStateChanged } from 'firebase/auth';
import { auth } from '@/config/firebase';
import { authService } from '@/services/authService';

interface AuthContextType {
  user: User | null;
  loading: boolean;
  signInWithEmail: (email: string, password: string) => Promise<void>;
  signUpWithEmail: (email: string, password: string, displayName?: string) => Promise<void>;
  signInWithGoogle: () => Promise<void>;
  signInWithApple: () => Promise<void>;
  signOut: () => Promise<void>;
  resetPassword: (email: string) => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: React.ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      setUser(user);
      setLoading(false);
    });

    return unsubscribe;
  }, []);

  const signInWithEmail = async (email: string, password: string) => {
    try {
    await authService.signInWithEmail(email, password);
    } catch (error: any) {
      // Re-throw with user-friendly message
      const errorCode = error?.code || '';
      let message = error?.message || 'Failed to sign in';
      
      if (errorCode === 'auth/user-not-found') {
        message = 'No account found with this email address.';
      } else if (errorCode === 'auth/wrong-password') {
        message = 'Incorrect password. Please try again.';
      } else if (errorCode === 'auth/invalid-email') {
        message = 'Invalid email address.';
      } else if (errorCode === 'auth/user-disabled') {
        message = 'This account has been disabled.';
      } else if (errorCode === 'auth/too-many-requests') {
        message = 'Too many failed attempts. Please try again later.';
      }
      
      throw new Error(message);
    }
  };

  const signUpWithEmail = async (
    email: string,
    password: string,
    displayName?: string
  ) => {
    try {
    await authService.signUpWithEmail(email, password, displayName);
    } catch (error: any) {
      // Re-throw with user-friendly message
      const errorCode = error?.code || '';
      let message = error?.message || 'Failed to create account';
      
      if (errorCode === 'auth/email-already-in-use') {
        message = 'An account with this email already exists.';
      } else if (errorCode === 'auth/invalid-email') {
        message = 'Invalid email address.';
      } else if (errorCode === 'auth/weak-password') {
        message = 'Password is too weak. Please use a stronger password.';
      } else if (errorCode === 'auth/operation-not-allowed') {
        message = 'Email/password accounts are not enabled.';
      }
      
      throw new Error(message);
    }
  };

  const signInWithGoogle = async () => {
    try {
    await authService.signInWithGoogle();
    } catch (error: any) {
      // Re-throw with user-friendly message
      const errorCode = error?.code || '';
      let message = error?.message || 'Failed to sign in with Google';
      
      if (errorCode === 'auth/popup-closed-by-user') {
        message = 'Sign-in popup was closed. Please try again.';
      } else if (errorCode === 'auth/cancelled-popup-request') {
        message = 'Only one popup request is allowed at a time.';
      } else if (errorCode === 'auth/popup-blocked') {
        message = 'Popup was blocked by your browser. Please allow popups for this site.';
      }
      
      throw new Error(message);
    }
  };

  const signInWithApple = async () => {
    try {
    await authService.signInWithApple();
    } catch (error: any) {
      // Re-throw with user-friendly message
      const errorCode = error?.code || '';
      let message = error?.message || 'Failed to sign in with Apple';
      
      if (errorCode === 'auth/popup-closed-by-user') {
        message = 'Sign-in popup was closed. Please try again.';
      } else if (errorCode === 'auth/cancelled-popup-request') {
        message = 'Only one popup request is allowed at a time.';
      } else if (errorCode === 'auth/popup-blocked') {
        message = 'Popup was blocked by your browser. Please allow popups for this site.';
      }
      
      throw new Error(message);
    }
  };

  const signOut = async () => {
    try {
    await authService.signOut();
    } catch (error: any) {
      console.error('Error signing out:', error);
      throw new Error('Failed to sign out. Please try again.');
    }
  };

  const resetPassword = async (email: string) => {
    try {
    await authService.resetPassword(email);
    } catch (error: any) {
      // Re-throw with user-friendly message
      const errorCode = error?.code || '';
      let message = error?.message || 'Failed to send password reset email';
      
      if (errorCode === 'auth/user-not-found') {
        message = 'No account found with this email address.';
      } else if (errorCode === 'auth/invalid-email') {
        message = 'Invalid email address.';
      }
      
      throw new Error(message);
    }
  };

  const value: AuthContextType = {
    user,
    loading,
    signInWithEmail,
    signUpWithEmail,
    signInWithGoogle,
    signInWithApple,
    signOut,
    resetPassword,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};


